import type { Menu } from '@/api/rbac';

/**
 * 公司账号（data_scope = COMPANY）的平台专用入口。
 *
 * 后端对公司账号的约束是「角色授予的权限码 ∩ 公司可用范围」：平台专用接口即使持有权限码也回
 * 403(90301)。权限码本身没有变，所以前端得自己知道哪些码 / 页面只对平台开放，登录后从
 * 权限集和菜单树里剔掉。漏剔不会泄露数据（后端兜底 403），只是体验不好。
 *
 * 来源：后端 feature/v1.0.2 f28f52ed 的 @PreAuthorize ——
 *   `@adminAuth.has(...)` = 仅平台，`companyScoped` / `sharedRead` = 公司也放行。
 * 下面只收「只出现在 has 里」的码；同一个码在别处被 companyScoped/sharedRead 用到的
 * （如 admin:alert:read、admin:audit:read、admin:plan:read），不能整码剔除，改按页面 path 剔。
 * 后端改了守卫要同步这里。
 */
export const PLATFORM_ONLY_PERMISSION_CODES: ReadonlySet<string> = new Set([
  // AI：手动跑批、价格、LLM 配置 / 模型 / 旧统计
  'admin:ai-metering:aggregate',
  'admin:ai-pricing:manage',
  'admin:ai-pricing:publish',
  'admin:ai-pricing:read',
  'admin:llm-config:create',
  'admin:llm-config:delete',
  'admin:llm-config:read',
  'admin:llm-config:update',
  'admin:llm-model:create',
  'admin:llm-model:delete',
  'admin:llm-model:read',
  'admin:llm-model:update',
  'admin:llm-stats:read',
  'admin:profile:create',
  'admin:profile:delete',
  'admin:profile:read',
  'admin:profile:update',
  // 告警规则 / 通知通道（告警事件公司可用，admin:alert:read 按 path 剔）
  'admin:alert:write',
  'admin:alert:channel:write',
  // 故事库审核：全部写读入口都经 AdminScopeGuard.permits，平台运营动作，公司账号一律拒绝
  'admin:story-review:read',
  'admin:story-review:submit',
  'admin:story-review:review',
  'admin:story-review:publish',
  'admin:story-review:media',
  'admin:story-review:check',
  'admin:story-review:publish-without-cover',
  // 审计导出、日历图、工厂账号、开放平台、支付接入
  'admin:audit:export',
  'admin:calendar:create',
  'admin:calendar:delete',
  'admin:calendar:read',
  'admin:factory-account:read',
  'admin:factory-account:write',
  'admin:open-app:create',
  'admin:open-app:read',
  'admin:open-app:secret',
  'admin:open-app:update',
  'admin:payment-access:read',
  'admin:payment-access:write',
  'admin:payment-credential:write',
  'admin:payment-test-access:write',
  // 主数据写操作（公司 / 品牌 / 产品 / SKU / 版本 / 能力集 / 预设表盘 / SKU 适配 / 预装应用组 / OTA 基线 / 计划）
  'admin:company:create',
  'admin:company:delete',
  'admin:company:update',
  'admin:brand:create',
  'admin:brand:delete',
  'admin:brand:update',
  'admin:product:create',
  'admin:product:delete',
  'admin:product:update',
  'admin:sku:create',
  'admin:sku:delete',
  'admin:sku:update',
  'admin:device-version:create',
  'admin:device-version:delete',
  'admin:device-version:update',
  'admin:capability:write',
  'admin:watch-custom-face:create',
  'admin:watch-custom-face:delete',
  'admin:watch-custom-face:update',
  'admin:sku-fitment:write',
  'admin:app-bundle:create',
  'admin:app-bundle:delete',
  'admin:app-bundle:update',
  'admin:ota-baseline:demote',
  'admin:ota-baseline:promote',
  'admin:plan:create',
  'admin:plan:delete',
  'admin:plan:update',
  'admin:feature-catalog:write',
  // 预装应用字典 / 包版本
  'admin:preinstall-app:create',
  'admin:preinstall-app:delete',
  'admin:preinstall-app:package-release',
  'admin:preinstall-app:package-upload',
  'admin:preinstall-app:read',
  'admin:preinstall-app:update',
  // 共享字典写操作（工厂 / 适配目标 / SN 方案 / 错误码）
  'admin:factory:create',
  'admin:factory:delete',
  'admin:factory:update',
  'admin:fitment-target:create',
  'admin:fitment-target:delete',
  'admin:fitment-target:update',
  'admin:sn-scheme:activate',
  'admin:sn-scheme:create',
  'admin:sn-scheme:dry-run',
  'admin:sn-scheme:freeze',
  'admin:sn-scheme:update',
  'admin:error-code:write',
  // SN 批次「实际适配目标」
  'admin:sn-batch:fitment-target',
]);

/**
 * 公司账号整页隐藏的路由（前缀匹配，含详情子路由）。
 * 多数页面的 :read 码已在上表里，这里兜住「读码与公司可用页面共用」的那几个，
 * 以及 RBAC 这类 superAdminOnly 页面（顺手列全，免得以后有人放宽守卫时漏掉）。
 */
export const PLATFORM_ONLY_PATHS: readonly string[] = [
  '/device/calendar',
  '/ops/alert-rules',
  '/ops/alert-channels',
  '/system/audit-log',
  '/ai/story-review',
  '/system/factory-accounts',
  '/system/payment-access',
  '/system/admin-user',
  '/system/role',
  '/system/menu',
  '/payment/access',
  '/open-platform',
  '/llm/configs',
  '/llm/models',
  '/llm/prices',
  '/llm/price-audit',
  '/llm/profile',
  '/catalog/preinstall-app',
];

export const isPlatformOnlyPath = (pathname: string): boolean =>
  PLATFORM_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/** 剔掉平台专用权限码。 */
export const stripPlatformOnlyCodes = (codes: string[]): string[] =>
  codes.filter((c) => !PLATFORM_ONLY_PERMISSION_CODES.has(c));

/**
 * 剔掉菜单树里平台专用的节点：path 命中平台页面的整棵子树、permission_code 是平台专用码的按钮。
 * 只剩脚手架的空 DIRECTORY 交给 BasicLayout 的 buildMenuFromTree 丢弃。
 */
export function prunePlatformOnlyMenus(menus: Menu[]): Menu[] {
  return menus
    .filter((m) => {
      const p = m.path?.trim();
      if (p && isPlatformOnlyPath(p)) return false;
      if (m.type === 'BUTTON' && m.permission_code && PLATFORM_ONLY_PERMISSION_CODES.has(m.permission_code)) {
        return false;
      }
      return true;
    })
    .map((m) => (m.children?.length ? { ...m, children: prunePlatformOnlyMenus(m.children) } : m));
}
